import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Form,
  FormGroup,
  Label,
} from "reactstrap";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { ReactSketchCanvas } from "react-sketch-canvas";
import Utils from "../../utils";
import {
  getLotDetails,
  getLotPartsList,
  lotSupplierAdd,
  lotSupplierRemove,
  lotSupplierSetDefault,
  lotVehicleAdd,
  lotVehicleRemove,
  lotVehicleSetDefault,
  lotWarehouseAdd,
  lotWarehouseRemove,
  lotImagesAdd,
  lotImagesSort,
  lotImagesDelete,
  lotVideosAdd,
  lotVideosSort,
  lotVideosDelete,
  updateLot,
} from "../../services/LotsServices";
import { getSupplierList, createSupplier } from "../../services/SuppliersTPServices";
import { getVehicleAllProcessList, getvehicleYearList } from "../../services/VehicleService";
import { getVehicleTypeList } from "../../services/VehicleType";
import {
  getVehicleMakesSearchDropdownList,
  getVehicleModelSearchDropdownList,
  getVehicleVariantsSearchDropdownList,
} from "../../services/SerachApiServices";
import { getWareHouseList } from "../../services/WareHouseServices";
import {
  actionCountryList,
  actionStateList,
  actionCityList,
} from "../../services/CommonService";
import SearchPicker from "./SearchPicker";

// ============================================================
// LotDetails — vista completa do lote.
// Hero em cima com preview + info, tab bar abaixo com 7 tabs.
// Cada tab tem ferramentas dedicadas a editar a relação com o lote.
// ============================================================

// ─── Helpers ──────────────────────────────────────────────────────

const fmtDate = (s) => (s ? s.substring(0, 10) : "—");
const fmtStatus = (t, s) =>
  s === 1 ? t("active") : s === 2 ? t("deleted") : "";
const fmtStorage = (t, s) =>
  s === 1
    ? t("reserved_at_supplier")
    : s === 2
    ? t("in_transport")
    : s === 3
    ? t("stored_at_facilities")
    : "";

const TAB_DEFS = [
  { id: "info", icon: "bi-info-circle", labelKey: "info" },
  { id: "parts", icon: "bi-box-seam", labelKey: "parts" },
  { id: "suppliers", icon: "bi-building", labelKey: "suppliers" },
  { id: "vehicles", icon: "bi-truck", labelKey: "vehicles" },
  { id: "warehouses", icon: "bi-shop", labelKey: "warehouses" },
  { id: "images", icon: "bi-image", labelKey: "images" },
  { id: "videos", icon: "bi-camera-video", labelKey: "videos" },
];

// ─── Page ─────────────────────────────────────────────────────────

const LotDetails = () => {
  const { idOrUuid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [lot, setLot] = useState(null);
  const [active, setActive] = useState("info");
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const fetchDetails = () => {
    if (!idOrUuid) return;
    setLoading(true);
    const params = {};
    if (/^\d+$/.test(idOrUuid)) params.id = parseInt(idOrUuid);
    else params.uuid = idOrUuid;
    getLotDetails(params)
      .then((res) => {
        if (res.data?.status === 200) setLot(res.data.data);
        else toast.error(res.data?.message || "Error");
        setLoading(false);
      })
      .catch((err) => {
        toast.error(Utils.getErrorMessage(err));
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idOrUuid]);

  if (loading) {
    return (
      <section className="content">
        <div className="container-fluid container-custom-height-new py-5 text-center text-muted">
          <i className="bi bi-arrow-clockwise fs-1 spin" />
          <div>{t("loading") || "Loading..."}</div>
        </div>
      </section>
    );
  }

  if (!lot) {
    return (
      <section className="content">
        <div className="container-fluid container-custom-height-new py-5 text-center text-muted">
          <i className="bi bi-exclamation-circle fs-1" />
          <div>{t("no_data_found") || "Lote não encontrado"}</div>
        </div>
      </section>
    );
  }

  const isDeleted = lot.status === 2;
  const heroBg = lot.images?.[0]?.image_url;

  return (
    <section className="content">
      <div className="container-fluid container-custom-height-new">
        {/* ─── Hero ─── */}
        <div
          className="card mb-3 overflow-hidden"
          style={{
            border: 0,
            boxShadow: "0 1px 3px rgba(0,0,0,.06)",
            background: "linear-gradient(135deg, #fef3e7 0%, #fff 60%)",
          }}
        >
          <div className="row g-0 align-items-stretch">
            <div className="col-md-3 d-flex" style={{ minHeight: 180 }}>
              {heroBg ? (
                <img
                  src={heroBg}
                  alt=""
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    minHeight: 180,
                  }}
                />
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center w-100 text-muted"
                  style={{
                    background:
                      "linear-gradient(135deg, #ffe5cc 0%, #ffcc99 100%)",
                    fontSize: 64,
                  }}
                >
                  <i className="bi bi-archive" />
                </div>
              )}
            </div>
            <div className="col-md-9">
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-2 flex-wrap gap-2">
                  <button
                    className="btn btn-sm btn-link text-muted p-0 me-1"
                    onClick={() => navigate("/lots")}
                  >
                    <i className="bi bi-arrow-left fs-5" />
                  </button>
                  {lot.lot_internal_id && (
                    <span
                      className="badge bg-primary"
                      style={{ fontSize: 13, padding: "6px 12px" }}
                    >
                      #{lot.lot_internal_id}
                    </span>
                  )}
                  <h3 className="mb-0 me-auto">{lot.name}</h3>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    title={t("edit_lot") || "Editar Lote"}
                    onClick={() => setEditOpen(true)}
                  >
                    <i className="bi bi-pencil-square" />
                  </button>
                  <span
                    className={`badge ${
                      isDeleted ? "bg-danger" : "bg-success"
                    }`}
                    style={{ fontSize: 12, padding: "6px 12px" }}
                  >
                    {fmtStatus(t, lot.status)}
                  </span>
                </div>
                {fmtStorage(t, lot.storage_status) && (
                  <div className="text-muted small mb-3">
                    <i className="bi bi-geo-alt me-1" />
                    {fmtStorage(t, lot.storage_status)}
                  </div>
                )}
                <div className="row g-2 mt-2">
                  <div className="col-sm-3">
                    <StatChip
                      icon="bi-box-seam"
                      label={t("parts")}
                      value={lot.parts_count || 0}
                    />
                  </div>
                  <div className="col-sm-3">
                    <StatChip
                      icon="bi-truck"
                      label={t("vehicles")}
                      value={(lot.vehicles || []).length}
                    />
                  </div>
                  <div className="col-sm-3">
                    <StatChip
                      icon="bi-building"
                      label={t("suppliers")}
                      value={(lot.suppliers || []).length}
                    />
                  </div>
                  <div className="col-sm-3">
                    <StatChip
                      icon="bi-calendar3"
                      label={t("purchase_date")}
                      value={fmtDate(lot.purchase_date)}
                      isText
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Tab bar ─── */}
        <div className="card mb-3" style={{ border: 0 }}>
          <div className="card-body p-2">
            <ul
              className="nav nav-pills flex-nowrap gap-1"
              style={{ overflowX: "auto" }}
            >
              {TAB_DEFS.map((tab) => (
                <li className="nav-item" key={tab.id}>
                  <button
                    className={`nav-link d-flex align-items-center gap-2 ${
                      active === tab.id ? "active" : ""
                    }`}
                    onClick={() => setActive(tab.id)}
                    style={{
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      border: 0,
                      background: active === tab.id ? "#0d6efd" : "transparent",
                      color: active === tab.id ? "white" : "#495057",
                    }}
                  >
                    <i className={`bi ${tab.icon}`} />
                    {t(tab.labelKey)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ─── Tab content ─── */}
        {active === "info" && <InfoTab lot={lot} t={t} />}
        {active === "parts" && <PartsTab lotId={lot.id} t={t} navigate={navigate} />}
        {active === "suppliers" && (
          <SuppliersTab lot={lot} t={t} onChanged={fetchDetails} />
        )}
        {active === "vehicles" && (
          <VehiclesTab lot={lot} t={t} onChanged={fetchDetails} />
        )}
        {active === "warehouses" && (
          <WarehousesTab lot={lot} t={t} onChanged={fetchDetails} />
        )}
        {active === "images" && (
          <ImagesTab lot={lot} t={t} onChanged={fetchDetails} />
        )}
        {active === "videos" && (
          <VideosTab lot={lot} t={t} onChanged={fetchDetails} />
        )}

        {/* Edit lot scalar fields modal */}
        {editOpen && (
          <EditLotModal
            lot={lot}
            t={t}
            onClose={() => setEditOpen(false)}
            onSaved={() => {
              setEditOpen(false);
              fetchDetails();
            }}
          />
        )}
      </div>
    </section>
  );
};

// ─── Edit Lot Modal ──────────────────────────────────────────────

const EditLotModal = ({ lot, t, onClose, onSaved }) => {
  const STORAGE_STATUS = [
    { id: 1, key: "reserved_at_supplier" },
    { id: 2, key: "in_transport" },
    { id: 3, key: "stored_at_facilities" },
  ];
  const LOT_STATUS = [
    { id: 1, key: "active" },
    { id: 2, key: "deleted" },
  ];
  const [form, setForm] = useState({
    id: lot.id,
    name: lot.name || "",
    lot_internal_id: lot.lot_internal_id || "",
    status: lot.status || 1,
    storage_status: lot.storage_status || 1,
    purchase_date: (lot.purchase_date || "").substring(0, 10),
    max_sale_date: (lot.max_sale_date || "").substring(0, 10),
    notes: lot.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error(`${t("please_enter")} ${t("name")}`);
      return;
    }
    setSaving(true);
    updateLot({
      id: form.id,
      name: form.name.trim(),
      lot_internal_id: form.lot_internal_id,
      status: form.status,
      storage_status: form.storage_status,
      purchase_date: form.purchase_date || null,
      max_sale_date: form.max_sale_date || null,
      notes: form.notes || null,
    })
      .then((res) => {
        toast.dismiss();
        toast.success(res.data?.message || t("Saved"));
        setSaving(false);
        onSaved();
      })
      .catch((err) => {
        setSaving(false);
        toast.error(Utils.getErrorMessage(err));
      });
  };

  return (
    <Modal isOpen toggle={onClose} centered>
      <Form onSubmit={submit}>
        <ModalHeader toggle={onClose}>
          {t("edit_lot") || "Editar Lote"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>{t("name")}</Label>
            <input
              className="form-control"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              autoFocus
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("lot_internal_id")}</Label>
            <input
              className="form-control"
              value={form.lot_internal_id}
              onChange={(e) =>
                setForm({ ...form, lot_internal_id: e.target.value })
              }
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("storage_status")}</Label>
            <select
              className="form-select"
              value={form.storage_status}
              onChange={(e) =>
                setForm({ ...form, storage_status: parseInt(e.target.value) })
              }
            >
              {STORAGE_STATUS.map((s) => (
                <option key={s.id} value={s.id}>
                  {t(s.key)}
                </option>
              ))}
            </select>
          </FormGroup>
          <FormGroup>
            <Label>{t("state")}</Label>
            <select
              className="form-select"
              value={form.status}
              onChange={(e) =>
                setForm({ ...form, status: parseInt(e.target.value) })
              }
            >
              {LOT_STATUS.map((s) => (
                <option key={s.id} value={s.id}>
                  {t(s.key)}
                </option>
              ))}
            </select>
          </FormGroup>
          <div className="row">
            <div className="col-md-6">
              <FormGroup>
                <Label>{t("purchase_date")}</Label>
                <input
                  type="date"
                  className="form-control"
                  value={form.purchase_date}
                  onChange={(e) =>
                    setForm({ ...form, purchase_date: e.target.value })
                  }
                />
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label>{t("max_sale_date")}</Label>
                <input
                  type="date"
                  className="form-control"
                  value={form.max_sale_date}
                  onChange={(e) =>
                    setForm({ ...form, max_sale_date: e.target.value })
                  }
                />
              </FormGroup>
            </div>
          </div>
          <FormGroup>
            <Label>{t("notes")}</Label>
            <textarea
              className="form-control"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button color="primary" type="submit" disabled={saving}>
            {saving ? "..." : t("save")}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

// ─── Shared ──────────────────────────────────────────────────────

const StatChip = ({ icon, label, value, isText }) => (
  <div
    className="d-flex align-items-center gap-2 p-2 rounded"
    style={{ background: "rgba(255,255,255,0.7)" }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        background: "#fff3e0",
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#f57c00",
      }}
    >
      <i className={`bi ${icon} fs-5`} />
    </div>
    <div>
      <div className="text-muted small lh-1">{label}</div>
      <div
        className="fw-bold lh-1 mt-1"
        style={{ fontSize: isText ? 14 : 18 }}
      >
        {value}
      </div>
    </div>
  </div>
);

const TabCardHeader = ({ title, action }) => (
  <div className="d-flex align-items-center justify-content-between mb-3">
    <h5 className="mb-0">{title}</h5>
    {action}
  </div>
);

const EmptyMsg = ({ msg }) => (
  <div className="text-center text-muted py-5">
    <i className="bi bi-inbox fs-1 d-block mb-2" />
    {msg}
  </div>
);

// ─── Info Tab ────────────────────────────────────────────────────

const InfoTab = ({ lot, t }) => (
  <div className="card" style={{ border: 0 }}>
    <div className="card-body p-4">
      <div className="row g-3">
        <Field label={t("purchase_date")} value={fmtDate(lot.purchase_date)} />
        <Field label={t("max_sale_date")} value={fmtDate(lot.max_sale_date)} />
        <Field label={t("parts")} value={String(lot.parts_count || 0)} />
        <Field label={t("storage_status")} value={fmtStorage(t, lot.storage_status)} />
      </div>
      {lot.notes && (
        <div className="mt-4 pt-3 border-top">
          <div className="text-muted small text-uppercase fw-semibold mb-2">
            {t("notes")}
          </div>
          <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
            {lot.notes}
          </p>
        </div>
      )}
    </div>
  </div>
);
const Field = ({ label, value }) => (
  <div className="col-md-6 col-lg-3">
    <div className="text-muted small text-uppercase fw-semibold mb-1">
      {label}
    </div>
    <div className="fw-semibold">{value}</div>
  </div>
);

// ─── Parts Tab ───────────────────────────────────────────────────

const PartsTab = ({ lotId, t, navigate }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(0);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetch_ = () => {
    setLoading(true);
    const params = { lot_id: lotId };
    if (search.trim()) params.search = search.trim();
    if (statusFilter > 0) params.inventory_status = statusFilter;
    getLotPartsList(params)
      .then((res) => {
        setItems(res.data?.data || []);
        setLoading(false);
      })
      .catch((err) => {
        toast.error(Utils.getErrorMessage(err));
        setLoading(false);
      });
  };
  useEffect(() => {
    fetch_();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotId, statusFilter]);

  const onSearch = (v) => {
    setSearch(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetch_, 300);
  };

  return (
    <div className="card" style={{ border: 0 }}>
      <div className="card-body">
        <div className="d-flex flex-wrap gap-2 mb-3">
          <input
            className="form-control"
            placeholder={t("search")}
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
          <select
            className="form-select"
            style={{ width: "auto" }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(parseInt(e.target.value))}
          >
            <option value={0}>{t("all")}</option>
            <option value={1}>{t("in_stock")}</option>
            <option value={2}>{t("reserved")}</option>
            <option value={4}>{t("sold")}</option>
          </select>
        </div>
        {loading && <div className="text-center text-muted py-3">...</div>}
        {!loading && items.length === 0 ? (
          <EmptyMsg msg={t("no_data_found")} />
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>{t("name")}</th>
                  <th>{t("vehicle")}</th>
                  <th className="text-end">Qty</th>
                  <th className="text-end">{t("price")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr
                    key={p.id}
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      p.uuid && navigate(`/parts-inventory-details/${p.uuid}`)
                    }
                  >
                    <td>
                      {p.parts_internal_id && (
                        <span className="badge bg-primary">
                          #{p.parts_internal_id}
                        </span>
                      )}
                    </td>
                    <td>{p.part_name_translated || p.part_name || "—"}</td>
                    <td className="text-muted small">
                      {p.vehicle_internal_id && (
                        <span>
                          <i className="bi bi-truck" /> #{p.vehicle_internal_id}
                        </span>
                      )}
                      {p.registration_plate && ` (${p.registration_plate})`}
                    </td>
                    <td className="text-end">{p.quantity || 0}</td>
                    <td className="text-end">€{p.part_price || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Suppliers Tab ───────────────────────────────────────────────

const SuppliersTab = ({ lot, t, onChanged }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [available, setAvailable] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const loadAvailable = (q) => {
    setLoading(true);
    getSupplierList({ limit: 100, page: 1, search: q || "" })
      .then((res) => {
        setAvailable(res.data?.data || []);
        setLoading(false);
      })
      .catch((err) => {
        toast.error(Utils.getErrorMessage(err));
        setLoading(false);
      });
  };

  const openPicker = () => {
    setPickerOpen(true);
    setSearch("");
    loadAvailable("");
  };

  const debounceRef = useRef(null);
  const onSearchChange = (v) => {
    setSearch(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => loadAvailable(v), 300);
  };

  const addSupplier = (id) => {
    lotSupplierAdd({ lot_id: lot.id, supplier_id: id })
      .then((res) => {
        toast.dismiss();
        toast.success(res.data?.message || t("Saved"));
        setPickerOpen(false);
        onChanged();
      })
      .catch((err) => {
        toast.dismiss();
        toast.error(Utils.getErrorMessage(err));
      });
  };
  const removeSupplier = (linkId) => {
    lotSupplierRemove({ id: linkId })
      .then(() => onChanged())
      .catch((err) => toast.error(Utils.getErrorMessage(err)));
  };
  // setDefaultSupplier: o backend espera { lot_id, supplier_id }.
  // No model devolvido, `id` é o supplier_id (suppliers.id) e `link_id`
  // é o lot_suppliers.id. Para definir default, passamos supplier_id.
  const setDefault_ = (supplierId) => {
    lotSupplierSetDefault({ lot_id: lot.id, supplier_id: supplierId })
      .then(() => onChanged())
      .catch((err) => toast.error(Utils.getErrorMessage(err)));
  };

  return (
    <div className="card" style={{ border: 0 }}>
      <div className="card-body p-4">
        <TabCardHeader
          title={t("suppliers")}
          action={
            <Button color="primary" size="sm" onClick={openPicker}>
              <i className="bi bi-plus-lg me-1" /> {t("add_supplier")}
            </Button>
          }
        />
        {(lot.suppliers || []).length === 0 ? (
          <EmptyMsg msg={t("no_data_found")} />
        ) : (
          <ul className="list-group list-group-flush">
            {lot.suppliers.map((s) => (
              <li
                key={s.link_id}
                className="list-group-item d-flex align-items-center gap-3"
                style={{ paddingLeft: 0, paddingRight: 0 }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    background: "#e8f4fd",
                    color: "#0d6efd",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <i className="bi bi-building fs-5" />
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-2">
                    <strong>{s.name}</strong>
                    {s.is_default === 1 && (
                      <span className="badge bg-primary">{t("default")}</span>
                    )}
                  </div>
                  <div className="text-muted small">
                    {[
                      s.tua_number ? `TUA: ${s.tua_number}` : null,
                      s.nif ? `NIF: ${s.nif}` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
                <div className="d-flex gap-1">
                  {s.is_default !== 1 && (
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setDefault_(s.id)}
                      title={t("set_as_default")}
                    >
                      <i className="bi bi-star" />
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeSupplier(s.link_id)}
                  >
                    <i className="bi bi-trash3" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Picker modal */}
      <Modal
        isOpen={pickerOpen}
        toggle={() => setPickerOpen(false)}
        centered
        size="md"
      >
        <ModalHeader toggle={() => setPickerOpen(false)}>
          {t("add_supplier")}
        </ModalHeader>
        <ModalBody>
          <div className="d-flex gap-2 mb-3">
            <input
              className="form-control"
              placeholder={t("search")}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              autoFocus
            />
            <Button
              color="primary"
              outline
              onClick={() => setCreateOpen(true)}
              style={{ whiteSpace: "nowrap" }}
            >
              <i className="bi bi-plus-lg me-1" />
              {t("new_supplier") || "Novo Fornecedor"}
            </Button>
          </div>
          <div style={{ maxHeight: 380, overflowY: "auto" }}>
            {loading && <div className="text-center text-muted py-3">...</div>}
            {!loading && available.length === 0 && (
              <div className="text-center text-muted py-4">
                <i className="bi bi-building fs-1 d-block mb-2 opacity-25" />
                {t("no_data_found")}
              </div>
            )}
            {!loading &&
              available.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="list-group-item list-group-item-action text-start w-100 d-flex align-items-center gap-2"
                  onClick={() => addSupplier(s.id)}
                  style={{
                    border: 0,
                    borderBottom: "1px solid #e9ecef",
                    padding: "12px 8px",
                  }}
                >
                  <i className="bi bi-building text-primary" />
                  <div>
                    <div className="fw-semibold">{s.name}</div>
                    <div className="text-muted small">
                      {[s.country_name, s.tua_number ? `TUA: ${s.tua_number}` : null]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </ModalBody>
      </Modal>

      {createOpen && (
        <NewSupplierInlineModal
          t={t}
          onClose={() => setCreateOpen(false)}
          onCreated={(s) => {
            setCreateOpen(false);
            // Auto-associa o fornecedor recém-criado ao lote.
            if (s?.id) addSupplier(s.id);
            else loadAvailable(search);
          }}
        />
      )}
    </div>
  );
};

// ─── Novo Fornecedor (inline) ────────────────────────────────────

const NewSupplierInlineModal = ({ t, onClose, onCreated }) => {
  // Erros são localizados client-side: backend devolve "name is required"
  // / "country_id is required" em inglês — fazemos remap para PT/EN
  // baseado em substring da mensagem.
  const [form, setForm] = useState({
    name: "",
    country_id: 0,
    country_name: "",
    district_id: 0,
    district_name: "",
    has_districts: null, // null=desconhecido, false=zero, true=existe pelo menos 1
    city_id: 0,
    city_name: "",
    has_cities: null,
    nif: "",
    tua_number: "",
    address: "",
    notes: "",
    is_default: 0,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // Probe de existência de distritos/cidades. Para países sem distritos
  // (ex: Andorra), o picker é escondido. Para distritos sem cidades,
  // idem com o picker de cidades.
  useEffect(() => {
    if (!form.country_id) {
      setForm((p) => ({
        ...p,
        district_id: 0,
        district_name: "",
        has_districts: null,
        city_id: 0,
        city_name: "",
        has_cities: null,
      }));
      return;
    }
    actionStateList({ country_id: form.country_id })
      .then((res) => {
        const list = res.data?.data || [];
        setForm((p) => ({ ...p, has_districts: list.length > 0 }));
      })
      .catch(() => setForm((p) => ({ ...p, has_districts: false })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.country_id]);

  useEffect(() => {
    if (!form.district_id) {
      setForm((p) => ({ ...p, has_cities: null, city_id: 0, city_name: "" }));
      return;
    }
    actionCityList({ state_id: form.district_id })
      .then((res) => {
        const list = res.data?.data || [];
        setForm((p) => ({ ...p, has_cities: list.length > 0 }));
      })
      .catch(() => setForm((p) => ({ ...p, has_cities: false })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.district_id]);

  const fetchCountries = (search) =>
    actionCountryList({ limit: 100, page: 1, search: search || "" });
  const fetchStates = (search) =>
    actionStateList({
      country_id: form.country_id,
      search: search || "",
    });
  const fetchCities = (search) =>
    actionCityList({
      state_id: form.district_id,
      search: search || "",
    });

  const localizeError = (raw) => {
    const s = (raw || "").toString().toLowerCase();
    if (s.includes("name is required"))
      return t("please_enter") + " " + t("name");
    if (s.includes("country_id is required"))
      return t("please_select") + " " + t("country");
    return raw;
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setErr(`${t("please_enter") || "Por favor preenche"} ${t("name") || "Nome"}`);
      return;
    }
    if (!form.country_id) {
      setErr(`${t("please_select") || "Por favor selecciona"} ${t("country") || "País"}`);
      return;
    }
    setSaving(true);
    setErr(null);
    createSupplier({
      name: form.name.trim(),
      country_id: form.country_id,
      district_id: form.district_id || null,
      city_id: form.city_id || null,
      nif: form.nif || null,
      tua_number: form.tua_number || null,
      address: form.address || null,
      notes: form.notes || null,
      is_default: form.is_default,
    })
      .then((res) => {
        setSaving(false);
        toast.dismiss();
        toast.success(res.data?.message || t("Saved"));
        onCreated(res.data?.data || null);
      })
      .catch((e) => {
        setSaving(false);
        setErr(localizeError(Utils.getErrorMessage(e)));
      });
  };

  return (
    <Modal isOpen toggle={onClose} centered>
      <Form onSubmit={submit}>
        <ModalHeader toggle={onClose}>
          {t("new_supplier") || "Novo Fornecedor"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>{t("name")}</Label>
            <input
              className="form-control"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              autoFocus
            />
          </FormGroup>

          <div className="mb-3">
            <SearchPicker
              label={t("country") || "País"}
              selectedLabel={form.country_name}
              placeholder="—"
              fetchFn={fetchCountries}
              itemKey={(c) => c.id}
              itemLabel={(c) => c.name}
              modalTitle={t("country") || "País"}
              searchPlaceholder={t("search") || "Pesquisar..."}
              onPick={(c) =>
                setForm((p) => ({
                  ...p,
                  country_id: c.id,
                  country_name: c.name,
                  district_id: 0,
                  district_name: "",
                  city_id: 0,
                  city_name: "",
                }))
              }
            />
          </div>

          {/* Distrito (apenas se o país tiver pelo menos 1) */}
          {form.has_districts && (
            <div className="mb-3">
              <SearchPicker
                label={t("district") || "Distrito"}
                selectedLabel={form.district_name}
                placeholder="—"
                fetchFn={fetchStates}
                itemKey={(s) => s.id}
                itemLabel={(s) => s.name}
                modalTitle={t("district") || "Distrito"}
                searchPlaceholder={t("search") || "Pesquisar..."}
                onPick={(s) =>
                  setForm((p) => ({
                    ...p,
                    district_id: s.id,
                    district_name: s.name,
                    city_id: 0,
                    city_name: "",
                  }))
                }
              />
            </div>
          )}

          {/* Cidade (apenas se distrito existe e tem cidades) */}
          {form.district_id > 0 && form.has_cities && (
            <div className="mb-3">
              <SearchPicker
                label={t("city") || "Cidade"}
                selectedLabel={form.city_name}
                placeholder="—"
                fetchFn={fetchCities}
                itemKey={(c) => c.id}
                itemLabel={(c) => c.name}
                modalTitle={t("city") || "Cidade"}
                searchPlaceholder={t("search") || "Pesquisar..."}
                onPick={(c) =>
                  setForm((p) => ({
                    ...p,
                    city_id: c.id,
                    city_name: c.name,
                  }))
                }
              />
            </div>
          )}

          <div className="row">
            <div className="col-md-6">
              <FormGroup>
                <Label>NIF</Label>
                <input
                  className="form-control"
                  value={form.nif}
                  onChange={(e) => setForm({ ...form, nif: e.target.value })}
                />
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label>{t("tua_number")}</Label>
                <input
                  className="form-control"
                  value={form.tua_number}
                  onChange={(e) =>
                    setForm({ ...form, tua_number: e.target.value })
                  }
                />
              </FormGroup>
            </div>
          </div>
          <FormGroup>
            <Label>{t("address")}</Label>
            <input
              className="form-control"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("notes")}</Label>
            <textarea
              className="form-control"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </FormGroup>
          <FormGroup check>
            <Label check>
              <input
                type="checkbox"
                checked={form.is_default === 1}
                onChange={(e) =>
                  setForm({ ...form, is_default: e.target.checked ? 1 : 0 })
                }
              />{" "}
              {t("set_as_default")}
            </Label>
          </FormGroup>
          {err && <div className="alert alert-danger py-2 mt-2 mb-0">{err}</div>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button color="primary" type="submit" disabled={saving}>
            {saving ? "..." : t("save")}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

// ─── Vehicles Tab ────────────────────────────────────────────────

const VehiclesTab = ({ lot, t, onChanged }) => {
  const [addMode, setAddMode] = useState(null); // 'existing' | 'lite' | null

  const removeVehicle = (linkId) => {
    lotVehicleRemove({ id: linkId })
      .then(() => onChanged())
      .catch((err) => toast.error(Utils.getErrorMessage(err)));
  };
  // setDefaultVehicle: backend espera { lot_id, id } onde `id` é o
  // lot_vehicles.id (link).
  const setDefault_ = (linkId) => {
    lotVehicleSetDefault({ lot_id: lot.id, id: linkId })
      .then(() => onChanged())
      .catch((err) => toast.error(Utils.getErrorMessage(err)));
  };

  const display = (v) => {
    const isLite = !v.vehicle_id;
    if (!isLite) {
      const internal = v.vehicle_internal_id || "";
      const plate = v.registration_plate || v.registration || "";
      return internal
        ? `#${internal} · ${plate || v.vehicle_make_name || ""}`
        : plate;
    }
    return [v.vehicle_make_name, v.vehicle_model_name, v.vehicle_year]
      .filter(Boolean)
      .join(" · ") || "—";
  };

  return (
    <div className="card" style={{ border: 0 }}>
      <div className="card-body p-4">
        <TabCardHeader
          title={t("vehicles")}
          action={
            <div className="d-flex gap-2">
              <Button
                color="primary"
                size="sm"
                onClick={() => setAddMode("existing")}
              >
                <i className="bi bi-truck me-1" /> {t("existing_vehicle")}
              </Button>
              <Button
                color="outline-primary"
                size="sm"
                onClick={() => setAddMode("lite")}
              >
                <i className="bi bi-pencil-square me-1" /> {t("lite_vehicle")}
              </Button>
            </div>
          }
        />
        {(lot.vehicles || []).length === 0 ? (
          <EmptyMsg msg={t("no_data_found")} />
        ) : (
          <ul className="list-group list-group-flush">
            {lot.vehicles.map((v) => {
              const isLite = !v.vehicle_id;
              return (
                <li
                  key={v.id}
                  className="list-group-item d-flex align-items-center gap-3"
                  style={{ paddingLeft: 0, paddingRight: 0 }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      background: isLite ? "#fff3e0" : "#e8f4fd",
                      color: isLite ? "#f57c00" : "#0d6efd",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <i className={`bi ${isLite ? "bi-pencil" : "bi-truck"} fs-5`} />
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2">
                      <strong>{display(v)}</strong>
                      {v.is_default === 1 && (
                        <span className="badge bg-primary">{t("default")}</span>
                      )}
                    </div>
                    <div className="text-muted small">
                      {isLite ? t("lite_vehicle") : t("existing_vehicle")}
                      {v.motorization && ` · ${v.motorization}`}
                    </div>
                  </div>
                  <div className="d-flex gap-1">
                    {v.is_default !== 1 && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setDefault_(v.id)}
                        title={t("set_as_default")}
                      >
                        <i className="bi bi-star" />
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeVehicle(v.id)}
                    >
                      <i className="bi bi-trash3" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {addMode === "existing" && (
        <ExistingVehiclePicker
          lot={lot}
          t={t}
          onClose={() => setAddMode(null)}
          onAdded={() => {
            setAddMode(null);
            onChanged();
          }}
        />
      )}
      {addMode === "lite" && (
        <LiteVehicleForm
          lot={lot}
          t={t}
          onClose={() => setAddMode(null)}
          onAdded={() => {
            setAddMode(null);
            onChanged();
          }}
        />
      )}
    </div>
  );
};

const ExistingVehiclePicker = ({ lot, t, onClose, onAdded }) => {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetch_ = (q) => {
    setLoading(true);
    const params = {
      limit: 30,
      page: 1,
      search: "",
      free_search: q || "",
      vehicle_status_filter: 1,
    };
    getVehicleAllProcessList(params)
      .then((res) => {
        setItems(res.data?.data || []);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(Utils.getErrorMessage(err));
      });
  };
  useEffect(() => {
    fetch_("");
  }, []);
  const onSearch = (v) => {
    setSearch(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetch_(v), 300);
  };

  const pick = (vId) => {
    lotVehicleAdd({ lot_id: lot.id, vehicle_id: vId })
      .then(() => onAdded())
      .catch((err) => toast.error(Utils.getErrorMessage(err)));
  };

  return (
    <Modal isOpen toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>{t("existing_vehicle")}</ModalHeader>
      <ModalBody>
        <input
          className="form-control mb-3"
          placeholder={
            t("search_placeholder_vehicle") ||
            "Pesquisa por ID TP, marca, modelo, matrícula viatura..."
          }
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          autoFocus
        />
        <div style={{ maxHeight: 480, overflowY: "auto" }}>
          {loading && <div className="text-center text-muted py-3">...</div>}
          {!loading && items.length === 0 && (
            <div className="text-center text-muted py-4">
              {t("no_data_found")}
            </div>
          )}
          {items.map((r) => {
            const internal = r.vehicle_internal_id || "";
            const plate = r.registration_plate || r.registration || "";
            const make = r.vehicle_make_name || r.make_name || "";
            const model = r.vehicle_model_name || r.vehicle_model || "";
            const year = r.vehicle_year || "";
            const vin = r.vin_number || "";
            return (
              <button
                key={r.id}
                type="button"
                className="list-group-item list-group-item-action text-start w-100 d-flex align-items-center gap-2"
                onClick={() => pick(r.id)}
                style={{
                  border: 0,
                  borderBottom: "1px solid #e9ecef",
                  padding: "12px 8px",
                }}
              >
                <i className="bi bi-truck text-primary" />
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {internal && (
                      <span className="badge bg-primary">#{internal}</span>
                    )}
                    <strong>{plate || `${make} ${model}`}</strong>
                  </div>
                  <div className="text-muted small">
                    {[make, model, year, vin && `VIN: ${vin}`]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </ModalBody>
    </Modal>
  );
};

const LiteVehicleForm = ({ lot, t, onClose, onAdded }) => {
  // Form: type → year → make → model → variant — todos via SearchPicker
  // (server-side search) para lidar com dropdowns de centenas de valores.
  const [form, setForm] = useState({
    vehicle_type_id: 0,
    vehicle_type_name: "",
    vehicle_year: "",
    vehicle_make_id: 0,
    vehicle_make_name: "",
    vehicle_model_id: 0,
    vehicle_model_name: "",
    variants_id: 0,
    variants_name: "",
    motorization: "",
    is_default: 0,
  });
  const [saving, setSaving] = useState(false);

  // Reset cascadas quando upstream muda.
  const resetDownstream = (level) => {
    setForm((p) => {
      const next = { ...p };
      if (level <= 1) {
        next.vehicle_year = "";
      }
      if (level <= 2) {
        next.vehicle_make_id = 0;
        next.vehicle_make_name = "";
      }
      if (level <= 3) {
        next.vehicle_model_id = 0;
        next.vehicle_model_name = "";
      }
      if (level <= 4) {
        next.variants_id = 0;
        next.variants_name = "";
      }
      return next;
    });
  };

  // Fetcher factories — fecham sobre form actual via callbacks porque
  // o SearchPicker memoiza, mas readiamos o estado em cada chamada.
  const fetchTypes = (search) =>
    getVehicleTypeList({ limit: 50, page: 1, search });
  // /vehicle-year/list ignora o param `search` (computa anos a partir
  // de min/max nos modelos), por isso filtramos client-side.
  const fetchYears = (search) =>
    getvehicleYearList({
      limit: 100,
      page: 1,
      search,
      vehicle_type_id: form.vehicle_type_id,
    }).then((res) => {
      const all = res?.data?.data ?? res?.data ?? [];
      const list = Array.isArray(all) ? all : [];
      if (!search) return { data: { data: list } };
      const s = String(search).toLowerCase();
      return {
        data: {
          data: list.filter((y) => {
            const v = typeof y === "object" ? y.year || y.vehicle_year : y;
            return String(v).toLowerCase().includes(s);
          }),
        },
      };
    });
  const fetchMakes = (search) =>
    getVehicleMakesSearchDropdownList({
      limit: 30,
      page: 1,
      search,
      vehicle_type_id: form.vehicle_type_id,
      year: form.vehicle_year,
    });
  const fetchModels = (search) =>
    getVehicleModelSearchDropdownList({
      limit: 30,
      page: 1,
      search,
      vehicle_type_id: form.vehicle_type_id,
      vehicle_year: form.vehicle_year,
      // O backend espera `make_id` (não `vehicle_make`).
      make_id: form.vehicle_make_id,
    });
  const fetchVariants = (search) =>
    getVehicleVariantsSearchDropdownList({
      limit: 30,
      page: 1,
      search,
      vehicle_type_id: form.vehicle_type_id,
      vehicle_year: form.vehicle_year,
      // O backend espera `model_id` (não `vehicle_model`).
      model_id: form.vehicle_model_id,
    });

  // Helpers de label específicos por endpoint:
  const typeLabel = (x) =>
    x.descriptionPT || x.description || x.type_name || x.name || "—";
  const yearLabel = (y) => (typeof y === "object" ? y.year || y.vehicle_year : String(y));
  const yearKey = (y) => (typeof y === "object" ? y.year || y.vehicle_year : y);
  const makeLabel = (x) => x.description || x.make_name || "—";
  const modelLabel = (x) => x.model_name || x.description || "—";
  const variantLabel = (x) => x.variants_name || x.description || "—";

  const submit = (e) => {
    e.preventDefault();
    if (!form.vehicle_model_id) {
      toast.error(`${t("please_select") || "Por favor selecciona"} ${t("vehiclemodel") || t("model") || "Modelo"}`);
      return;
    }
    setSaving(true);
    const payload = {
      lot_id: lot.id,
      vehicle_type_id: form.vehicle_type_id || null,
      vehicle_year: form.vehicle_year || null,
      vehicle_make_id: form.vehicle_make_id || null,
      vehicle_model_id: form.vehicle_model_id || null,
      variants_id: form.variants_id || null,
      motorization: form.motorization || null,
      is_default: form.is_default,
    };
    lotVehicleAdd(payload)
      .then(() => {
        setSaving(false);
        onAdded();
      })
      .catch((err) => {
        setSaving(false);
        toast.error(Utils.getErrorMessage(err));
      });
  };

  return (
    <Modal isOpen toggle={onClose} centered size="md">
      <Form onSubmit={submit}>
        <ModalHeader toggle={onClose}>{t("lite_vehicle")}</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <SearchPicker
              label={t("vehicle_type")}
              selectedLabel={form.vehicle_type_name}
              placeholder="—"
              fetchFn={fetchTypes}
              itemKey={(x) => x.id}
              itemLabel={typeLabel}
              modalTitle={t("vehicle_type")}
              searchPlaceholder={t("search") || "Pesquisar..."}
              onPick={(x) => {
                setForm((p) => ({
                  ...p,
                  vehicle_type_id: x.id,
                  vehicle_type_name: typeLabel(x),
                }));
                resetDownstream(1);
              }}
            />
          </div>
          <div className="mb-3">
            <SearchPicker
              label={t("vehicle_year")}
              selectedLabel={form.vehicle_year || ""}
              placeholder="—"
              disabled={!form.vehicle_type_id}
              fetchFn={fetchYears}
              itemKey={yearKey}
              itemLabel={yearLabel}
              modalTitle={t("vehicle_year")}
              searchPlaceholder={t("search") || "Pesquisar..."}
              onPick={(y) => {
                const val = yearKey(y);
                setForm((p) => ({ ...p, vehicle_year: val }));
                resetDownstream(2);
              }}
            />
          </div>
          <div className="mb-3">
            <SearchPicker
              label={t("vehicle_make")}
              selectedLabel={form.vehicle_make_name}
              placeholder="—"
              disabled={!form.vehicle_year}
              fetchFn={fetchMakes}
              itemKey={(x) => x.id}
              itemLabel={makeLabel}
              modalTitle={t("vehicle_make")}
              searchPlaceholder={t("search") || "Pesquisar..."}
              onPick={(x) => {
                setForm((p) => ({
                  ...p,
                  vehicle_make_id: x.id,
                  vehicle_make_name: makeLabel(x),
                }));
                resetDownstream(3);
              }}
            />
          </div>
          <div className="mb-3">
            <SearchPicker
              label={t("vehiclemodel") || t("model") || "Modelo"}
              selectedLabel={form.vehicle_model_name}
              placeholder="—"
              disabled={!form.vehicle_make_id}
              fetchFn={fetchModels}
              itemKey={(x) => x.id}
              itemLabel={modelLabel}
              modalTitle={t("vehiclemodel") || "Modelo"}
              searchPlaceholder={t("search") || "Pesquisar..."}
              onPick={(x) => {
                setForm((p) => ({
                  ...p,
                  vehicle_model_id: x.id,
                  vehicle_model_name: modelLabel(x),
                }));
                resetDownstream(4);
              }}
            />
          </div>
          <div className="mb-3">
            <SearchPicker
              label={t("vehicle_variant")}
              selectedLabel={form.variants_name}
              placeholder="—"
              disabled={!form.vehicle_model_id}
              fetchFn={fetchVariants}
              itemKey={(x) => x.id}
              itemLabel={variantLabel}
              modalTitle={t("vehicle_variant")}
              searchPlaceholder={t("search") || "Pesquisar..."}
              onPick={(x) =>
                setForm((p) => ({
                  ...p,
                  variants_id: x.id,
                  variants_name: variantLabel(x),
                }))
              }
            />
          </div>
          <FormGroup>
            <Label>{t("motorization") || "Motorização"}</Label>
            <input
              className="form-control"
              value={form.motorization}
              onChange={(e) => setForm({ ...form, motorization: e.target.value })}
            />
          </FormGroup>
          <FormGroup check>
            <Label check>
              <input
                type="checkbox"
                checked={form.is_default === 1}
                onChange={(e) =>
                  setForm({ ...form, is_default: e.target.checked ? 1 : 0 })
                }
              />{" "}
              {t("set_as_default")}
            </Label>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button color="primary" type="submit" disabled={saving}>
            {saving ? "..." : t("save")}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

// ─── Warehouses Tab ──────────────────────────────────────────────

const WarehousesTab = ({ lot, t, onChanged }) => {
  // Picker via SearchPicker — server-side search (limit 30) com filtro
  // por primeira hierarquia (parent_id IS NULL) feito client-side.
  const fetchWarehouses = (search) =>
    getWareHouseList({ limit: 50, page: 1, search: search || "" }).then(
      (res) => {
        const all = res.data?.data || [];
        const linked = new Set((lot.warehouses || []).map((x) => x.id));
        const filtered = all.filter(
          (w) =>
            (w.parent_id === null || w.parent_id === undefined) &&
            !linked.has(w.id),
        );
        return { data: { data: filtered } };
      },
    );

  const add = (warehouseId) => {
    lotWarehouseAdd({ lot_id: lot.id, warehouse_id: warehouseId })
      .then(() => {
        toast.dismiss();
        toast.success(t("Saved"));
        onChanged();
      })
      .catch((err) => toast.error(Utils.getErrorMessage(err)));
  };

  const remove = (linkId) => {
    lotWarehouseRemove({ id: linkId })
      .then(() => onChanged())
      .catch((err) => toast.error(Utils.getErrorMessage(err)));
  };

  // SearchPicker controla o seu próprio open/close mas precisa de um
  // trigger button — usamos um wrapper que mostra "+ Adicionar" como
  // se fosse o trigger.
  return (
    <div className="card" style={{ border: 0 }}>
      <div className="card-body p-4">
        <TabCardHeader
          title={t("warehouses")}
          action={
            <div style={{ minWidth: 240 }}>
              <SearchPicker
                selectedLabel=""
                placeholder={`+ ${t("add") || "Adicionar"} ${t("warehouses") || "Armazém"}`}
                fetchFn={fetchWarehouses}
                itemKey={(w) => w.id}
                itemLabel={(w) => w.warehouse_name}
                modalTitle={
                  t("select_warehouse") || "Seleccionar Armazém"
                }
                searchPlaceholder={t("search") || "Pesquisar..."}
                onPick={(w) => add(w.id)}
              />
            </div>
          }
        />
        {(lot.warehouses || []).length === 0 ? (
          <EmptyMsg msg={t("no_data_found")} />
        ) : (
          <ul className="list-group list-group-flush">
            {lot.warehouses.map((w) => (
              <li
                key={w.link_id}
                className="list-group-item d-flex align-items-center gap-3"
                style={{ paddingLeft: 0, paddingRight: 0 }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    background: "#e8f4fd",
                    color: "#0d6efd",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i className="bi bi-shop fs-5" />
                </div>
                <div className="flex-grow-1">
                  <strong>{w.name}</strong>
                </div>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => remove(w.link_id)}
                >
                  <i className="bi bi-trash3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ─── Images Tab ──────────────────────────────────────────────────

const ImagesTab = ({ lot, t, onChanged }) => {
  const fileInput = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(null); // { id, url }

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("lot_id", lot.id);
      Array.from(files).forEach((f) => fd.append("files", f));
      const res = await lotImagesAdd(fd);
      if (res.data?.status === 200) {
        toast.dismiss();
        toast.success(res.data.message);
        onChanged();
      } else toast.error(res.data?.message || "Error");
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    }
    setBusy(false);
    if (fileInput.current) fileInput.current.value = "";
  };

  const remove = (id) => {
    lotImagesDelete({ id })
      .then(() => onChanged())
      .catch((err) => toast.error(Utils.getErrorMessage(err)));
  };

  const move = (idx, dir) => {
    const items = [...(lot.images || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    const sortList = items.map((it, i) => ({ id: it.id, sort_order: i + 1 }));
    lotImagesSort({ sort_list: sortList })
      .then(() => onChanged())
      .catch((err) => toast.error(Utils.getErrorMessage(err)));
  };

  return (
    <div className="card position-relative" style={{ border: 0 }}>
      {/* Overlay enquanto upload está em curso — bloqueia interacção e
          informa o user para esperar (o S3 + processamento podem demorar). */}
      {busy && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255,255,255,0.85)",
            zIndex: 5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            borderRadius: 8,
          }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="text-muted" style={{ fontWeight: 500 }}>
            {t("uploading_image") ||
              "A enviar imagem(ns)... Por favor aguarda."}
          </div>
        </div>
      )}
      <div className="card-body p-4">
        <TabCardHeader
          title={t("images")}
          action={
            <>
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => upload(e.target.files)}
              />
              <Button
                color="primary"
                size="sm"
                onClick={() => fileInput.current?.click()}
                disabled={busy}
              >
                <i className="bi bi-plus-lg me-1" />{" "}
                {busy ? "..." : t("add") || "Adicionar"}
              </Button>
            </>
          }
        />
        {(lot.images || []).length === 0 ? (
          <EmptyMsg msg={t("no_data_found")} />
        ) : (
          <div className="row g-3">
            {lot.images.map((img, i) => (
              <div key={img.id} className="col-md-3 col-6">
                <div
                  className="position-relative bg-light rounded overflow-hidden"
                  style={{ aspectRatio: "1/1" }}
                >
                  <img
                    src={img.image_url}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      cursor: "pointer",
                    }}
                    onClick={() => setPreview(img.image_url)}
                  />
                  {/* Toolbar — sempre visível com fundo escuro */}
                  <div
                    className="position-absolute top-0 start-0 end-0 d-flex justify-content-between p-2"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, transparent 100%)",
                    }}
                  >
                    <div className="d-flex gap-1">
                      <button
                        className="btn btn-sm btn-light"
                        title={t("preview") || "Pré-visualizar"}
                        onClick={() => setPreview(img.image_url)}
                        style={{ padding: "2px 8px" }}
                      >
                        <i className="bi bi-eye" />
                      </button>
                      <button
                        className="btn btn-sm btn-light"
                        title={t("edit") || "Editar"}
                        onClick={() =>
                          setEditing({ id: img.id, url: img.image_url })
                        }
                        style={{ padding: "2px 8px" }}
                      >
                        <i className="bi bi-pencil-square" />
                      </button>
                    </div>
                    <button
                      className="btn btn-sm btn-danger"
                      title={t("delete") || "Eliminar"}
                      onClick={() => remove(img.id)}
                      style={{ padding: "2px 8px" }}
                    >
                      <i className="bi bi-trash3" />
                    </button>
                  </div>
                  {/* Reorder arrows on bottom */}
                  <div
                    className="position-absolute bottom-0 start-0 end-0 d-flex justify-content-between p-2"
                    style={{
                      background:
                        "linear-gradient(0deg, rgba(0,0,0,0.55) 0%, transparent 100%)",
                    }}
                  >
                    <button
                      className="btn btn-sm btn-light"
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      style={{ padding: "2px 8px" }}
                    >
                      <i className="bi bi-arrow-left" />
                    </button>
                    <button
                      className="btn btn-sm btn-light"
                      onClick={() => move(i, 1)}
                      disabled={i === lot.images.length - 1}
                      style={{ padding: "2px 8px" }}
                    >
                      <i className="bi bi-arrow-right" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Preview lightbox */}
        <Modal isOpen={!!preview} toggle={() => setPreview(null)} size="lg" centered>
          <ModalBody className="p-0 bg-dark">
            {preview && (
              <img
                src={preview}
                alt=""
                style={{ width: "100%", display: "block" }}
              />
            )}
          </ModalBody>
        </Modal>

        {/* Edit modal */}
        {editing && (
          <ImageEditorModal
            t={t}
            lot={lot}
            image={editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              onChanged();
            }}
          />
        )}
      </div>
    </div>
  );
};

// ─── Image Editor (crop+rotate) ──────────────────────────────────

const ImageEditorModal = ({ t, lot, image, onClose, onSaved }) => {
  // Modos: 'crop' | 'draw' | null
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [rotate, setRotate] = useState(0);
  const [mode, setMode] = useState(null);
  const [drawColor, setDrawColor] = useState("#ff0000");
  const [drawStroke, setDrawStroke] = useState(4);
  const [busy, setBusy] = useState(false);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    let blobUrl = null;
    let cancelled = false;
    // Tenta primeiro fetch+blob (resolve CORS ao escrever no canvas).
    // Se falhar (browser/S3 sem CORS), faz fallback para o URL directo
    // — mostra a imagem mas o canvas pode bloquear na operação de save
    // (taintedCanvas). O user vê a foto pelo menos.
    fetch(image.url, { mode: "cors" })
      .then((r) => {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.blob();
      })
      .then((b) => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(b);
        setSrc(blobUrl);
      })
      .catch(() => {
        if (!cancelled) setSrc(image.url);
      });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image.url]);

  // Aplica edição: aplica crop+rotate, depois faz overlay do desenho da
  // canvas (se em modo draw), e devolve um Blob JPEG.
  const apply = async () => {
    if (!imgRef.current) return;
    setBusy(true);
    try {
      let drawingBlob = null;
      if (mode === "draw" && canvasRef.current) {
        try {
          const dataUrl = await canvasRef.current.exportImage("png");
          drawingBlob = await fetch(dataUrl).then((r) => r.blob());
        } catch {
          /* sem desenho */
        }
      }
      const blob = await renderEditedBlob(
        imgRef.current,
        completedCrop,
        rotate,
        drawingBlob,
      );
      await lotImagesDelete({ id: image.id });
      const fd = new FormData();
      fd.append("lot_id", lot.id);
      fd.append("files", blob, `lot-${lot.id}-edited.jpg`);
      await lotImagesAdd(fd);
      toast.dismiss();
      toast.success(t("Saved"));
      onSaved();
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Modal isOpen toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>
        {t("edit") || "Editar imagem"}
      </ModalHeader>
      <ModalBody className="bg-dark text-center" style={{ minHeight: 420 }}>
        {!src ? (
          <div className="text-light py-5">...</div>
        ) : (
          <div
            style={{
              position: "relative",
              display: "inline-block",
              maxWidth: "100%",
              maxHeight: "60vh",
            }}
          >
            <ReactCrop
              crop={crop}
              onChange={(_, c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              disabled={mode !== "crop"}
            >
              <img
                ref={imgRef}
                src={src}
                alt=""
                style={{
                  maxWidth: "100%",
                  maxHeight: "60vh",
                  transform: `rotate(${rotate}deg)`,
                  display: "block",
                }}
                {...(src && src.startsWith("blob:")
                  ? { crossOrigin: "anonymous" }
                  : {})}
              />
            </ReactCrop>
            {mode === "draw" && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "auto",
                }}
              >
                <ReactSketchCanvas
                  ref={canvasRef}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: 0,
                    background: "transparent",
                  }}
                  canvasColor="transparent"
                  strokeColor={drawColor}
                  strokeWidth={drawStroke}
                />
              </div>
            )}
          </div>
        )}
        {/* Toolbar */}
        <div className="d-flex justify-content-center gap-3 mt-3 flex-wrap">
          <ToolBtn
            icon="bi-crop"
            label={t("crop") || "Recortar"}
            active={mode === "crop"}
            onClick={() => setMode(mode === "crop" ? null : "crop")}
          />
          <ToolBtn
            icon="bi-brush"
            label={t("draw") || "Desenhar"}
            active={mode === "draw"}
            onClick={() => setMode(mode === "draw" ? null : "draw")}
          />
          <ToolBtn
            icon="bi-arrow-counterclockwise"
            label="−90°"
            onClick={() => setRotate((r) => r - 90)}
          />
          <ToolBtn
            icon="bi-arrow-clockwise"
            label="+90°"
            onClick={() => setRotate((r) => r + 90)}
          />
          <ToolBtn
            icon="bi-arrow-counterclockwise"
            label={t("reset") || "Reset"}
            onClick={() => {
              setCrop(undefined);
              setCompletedCrop(null);
              setRotate(0);
              setMode(null);
              if (canvasRef.current) canvasRef.current.clearCanvas();
            }}
          />
          {mode === "draw" && (
            <>
              <input
                type="color"
                value={drawColor}
                onChange={(e) => setDrawColor(e.target.value)}
                title={t("color") || "Cor"}
                style={{
                  width: 36,
                  height: 36,
                  border: 0,
                  background: "transparent",
                  cursor: "pointer",
                }}
              />
              <input
                type="range"
                min="1"
                max="20"
                value={drawStroke}
                onChange={(e) => setDrawStroke(parseInt(e.target.value))}
                title={t("stroke") || "Espessura"}
                style={{ width: 100 }}
              />
              <button
                className="btn btn-sm btn-outline-light"
                onClick={() => canvasRef.current?.undo()}
                title={t("undo") || "Desfazer"}
              >
                <i className="bi bi-skip-backward" />
              </button>
              <button
                className="btn btn-sm btn-outline-light"
                onClick={() => canvasRef.current?.clearCanvas()}
                title={t("clear") || "Limpar"}
              >
                <i className="bi bi-eraser" />
              </button>
            </>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button color="primary" onClick={apply} disabled={busy || !src}>
          {busy ? "..." : t("save")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const ToolBtn = ({ icon, label, active, onClick }) => (
  <button
    type="button"
    className="d-flex flex-column align-items-center"
    onClick={onClick}
    style={{
      border: 0,
      background: "transparent",
      color: active ? "#0d6efd" : "white",
      cursor: "pointer",
      gap: 2,
      padding: "4px 8px",
    }}
  >
    <i className={`bi ${icon}`} style={{ fontSize: 22 }} />
    <span style={{ fontSize: 11 }}>{label}</span>
  </button>
);

// renderEditedBlob — aplica crop+rotate à imagem original; se houver
// drawingBlob (PNG da canvas de desenho), faz overlay no fim. Devolve JPEG.
async function renderEditedBlob(imageEl, crop, rotateDeg, drawingBlob) {
  const naturalW = imageEl.naturalWidth;
  const naturalH = imageEl.naturalHeight;
  const displayedW = imageEl.width;
  const displayedH = imageEl.height;
  const scaleX = naturalW / displayedW;
  const scaleY = naturalH / displayedH;

  const cw = crop?.width ? crop.width * scaleX : naturalW;
  const ch = crop?.height ? crop.height * scaleY : naturalH;
  const cx = crop?.x ? crop.x * scaleX : 0;
  const cy = crop?.y ? crop.y * scaleY : 0;

  const rad = (rotateDeg * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));
  const outW = Math.round(cw * cos + ch * sin);
  const outH = Math.round(cw * sin + ch * cos);

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.translate(outW / 2, outH / 2);
  ctx.rotate(rad);
  ctx.drawImage(imageEl, cx, cy, cw, ch, -cw / 2, -ch / 2, cw, ch);

  // Overlay drawing (em coords da viewport — escalamos para o canvas final)
  if (drawingBlob) {
    try {
      const drawImg = await blobToImage(drawingBlob);
      // Restaurar transform → desenhar drawing à escala da imagem final.
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(drawImg, 0, 0, outW, outH);
    } catch {
      /* ignore */
    }
  }

  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("canvas toBlob failed"))),
      "image/jpeg",
      0.92,
    ),
  );
}

function blobToImage(blob) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(blob);
  });
}

// ─── Videos Tab ──────────────────────────────────────────────────

const VideosTab = ({ lot, t, onChanged }) => {
  const fileInput = useRef(null);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(null);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("lot_id", lot.id);
      Array.from(files).forEach((f) => fd.append("files", f));
      const res = await lotVideosAdd(fd);
      if (res.data?.status === 200) {
        toast.dismiss();
        toast.success(res.data.message);
        onChanged();
      } else toast.error(res.data?.message || "Error");
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    }
    setBusy(false);
    if (fileInput.current) fileInput.current.value = "";
  };

  const remove = (id) => {
    lotVideosDelete({ id })
      .then(() => onChanged())
      .catch((err) => toast.error(Utils.getErrorMessage(err)));
  };

  const move = (idx, dir) => {
    const items = [...(lot.videos || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    const sortList = items.map((it, i) => ({ id: it.id, sort_order: i + 1 }));
    lotVideosSort({ sort_list: sortList })
      .then(() => onChanged())
      .catch((err) => toast.error(Utils.getErrorMessage(err)));
  };

  return (
    <div className="card" style={{ border: 0 }}>
      <div className="card-body p-4">
        <TabCardHeader
          title={t("videos")}
          action={
            <>
              <input
                ref={fileInput}
                type="file"
                accept="video/*"
                hidden
                onChange={(e) => upload(e.target.files)}
              />
              <Button
                color="primary"
                size="sm"
                onClick={() => fileInput.current?.click()}
                disabled={busy}
              >
                <i className="bi bi-plus-lg me-1" />{" "}
                {busy ? "..." : t("add") || "Adicionar"}
              </Button>
            </>
          }
        />
        {(lot.videos || []).length === 0 ? (
          <EmptyMsg msg={t("no_data_found")} />
        ) : (
          <div className="row g-3">
            {lot.videos.map((v, i) => (
              <div key={v.id} className="col-md-3 col-6">
                <div
                  className="position-relative rounded overflow-hidden"
                  style={{
                    aspectRatio: "1/1",
                    background: "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
                    cursor: "pointer",
                  }}
                  onClick={() => setPlaying(v.video_url)}
                >
                  <div
                    className="position-absolute top-50 start-50 translate-middle text-white"
                    style={{ fontSize: 48 }}
                  >
                    <i className="bi bi-play-circle-fill" />
                  </div>
                  {/* Toolbar — sempre visível */}
                  <div
                    className="position-absolute top-0 start-0 end-0 d-flex justify-content-between p-2"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, transparent 100%)",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="btn btn-sm btn-light"
                      onClick={() => setPlaying(v.video_url)}
                      title={t("play") || "Reproduzir"}
                      style={{ padding: "2px 8px" }}
                    >
                      <i className="bi bi-play-fill" />
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => remove(v.id)}
                      title={t("delete") || "Eliminar"}
                      style={{ padding: "2px 8px" }}
                    >
                      <i className="bi bi-trash3" />
                    </button>
                  </div>
                  <div
                    className="position-absolute bottom-0 start-0 end-0 d-flex justify-content-between p-2"
                    style={{
                      background:
                        "linear-gradient(0deg, rgba(0,0,0,0.55) 0%, transparent 100%)",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="btn btn-sm btn-light"
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      style={{ padding: "2px 8px" }}
                    >
                      <i className="bi bi-arrow-left" />
                    </button>
                    <button
                      className="btn btn-sm btn-light"
                      onClick={() => move(i, 1)}
                      disabled={i === lot.videos.length - 1}
                      style={{ padding: "2px 8px" }}
                    >
                      <i className="bi bi-arrow-right" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Modal isOpen={!!playing} toggle={() => setPlaying(null)} size="lg" centered>
          <ModalBody className="p-0 bg-dark">
            {playing && (
              <video
                src={playing}
                controls
                autoPlay
                style={{ width: "100%", display: "block" }}
              />
            )}
          </ModalBody>
        </Modal>
      </div>
    </div>
  );
};

export default LotDetails;
